import { Box, Button, Flex, Heading, HStack, Icon, Stack, Text, useToast } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { useQueryClient } from '@tanstack/react-query';
import InputCopyField from 'components/form-elements/InputCopyField';
import AlertModal from 'components/modals/AlertModal';
import SystemPartnerCard from 'components/SystemPartnerCard';
import localconfig from 'config';
import { useAxios } from 'context/AxiosContextProvider';
import { useUserContext } from 'context/UserContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import useCheckTaskMutation from 'hooks/mutations/useCheckTaskMutation';
import useDeleteServiceDocumentMutation from 'hooks/mutations/useDeleteServiceDocumentMutation';
import useUploadFileMutation from 'hooks/mutations/useUploadFileMutation';
import useAccountV2Query from 'hooks/useAccountV2Query';
import { get } from 'lodash';
import { LanguageHighlight } from 'pages/serviceRecord/LanguageHighlight';
import RecentlyCheckedCard from 'pages/tasks/RecentlyCheckedCard';
import TaskBannerCard from 'pages/tasks/TaskBannerCard';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { IoIosWarning } from 'react-icons/io';
import { useNavigate, useParams } from 'react-router-dom';
import { useCopyToClipboard } from 'usehooks-ts';

import { DetailPageTask, Task } from '../../api/types';
import ActivityLogCard from '../../components/ActivityLogCard';
import { ServiceHistoryRequestIcon } from '../../components/Icons';
import Link from '../../components/Link';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { StatusCard } from '../../components/StatusCard';
import { TasksTableCard } from '../../components/TasksTableCard';
import { Warning } from '../../components/Warning';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import { getStatus } from '../../helpers/general';
import useAssignMutation from '../../hooks/mutations/useAssignMutation';
import useAssignNextTaskMutation from '../../hooks/mutations/useAssignNextTaskMutation';
import { useAttachFallbackEmptyServiceHistoryMutation } from '../../hooks/mutations/useAttachFallbackEmptyServiceHistoryMutation';
import useUpdateServiceHistoryRequestStatusMutation from '../../hooks/mutations/useUpdateServiceHistoryRequestStatusMutation';
import useServiceHistoryQuery from '../../hooks/useServiceHistoryQuery';
import { ContentCard } from '../../layout/ContentCard';
import { ContentLayout } from '../../layout/ContentLayout';
import PageMetatags from '../../layout/PageMetatags';
import FileDisplay from '../FileDisplay';
import FileForm from '../FileForm';
import CredentialCard from '../workshop/CredentialCard';
import WorkshopCard from '../workshop/WorkshopCard';
import WorkshopWarning from '../workshop/WorkshopWarning';

const ServiceHistoryDetailsPage = () => {
  const { statusCodes, user, isMember, opUsers, statusTransitions } = useUserContext();
  const { t } = useTranslation();
  const toast = useToast();
  const axios = useAxios();
  const { id, type } = useParams();
  const navigate = useNavigate();
  const [canEdit, setCanEdit] = useState(false);
  const [applyAnnimation, setApplyAnnimation] = useState(false);
  const queryClient = useQueryClient();

  const serviceHistoryId = Number(id);

  const [value, copy] = useCopyToClipboard();
  const serviceHistoryQuery = useServiceHistoryQuery(serviceHistoryId);
  const tasks = (serviceHistoryQuery.data as { tasks?: DetailPageTask[] } | undefined)?.tasks;

  const accountQuery = useAccountV2Query(serviceHistoryQuery.data?.account_id);
  const queryToInvalidate = () => queryClient.invalidateQueries({ queryKey: useServiceHistoryQuery.getKey(serviceHistoryId) });

  const attachFallbackMutation = useAttachFallbackEmptyServiceHistoryMutation(serviceHistoryId);

  const sendRequest = async ({ url, method = 'put', dataForEndpoint = {} }: { url: string; method?: string; dataForEndpoint?: any }) => {
    const axiosCall =
      method !== 'delete'
        ? // @ts-ignore
          axios[method](url, dataForEndpoint)
        : // @ts-ignore
          axios[method](url);
    return await axiosCall
      .catch((err: { response: { status: number } }) => {
        manageErrorResponse(toast, err.response);
        return Promise.reject(err);
      })
      // @ts-expect-error
      .then(({ data }) => {
        queryClient.invalidateQueries({ queryKey: useAccountV2Query.getKey(accountQuery.data?.id as number) });
        queryClient.invalidateQueries({
          queryKey: useServiceHistoryQuery.getKey(serviceHistoryId),
        });
        return data;
      });
  };

  const isSystemPartnerFull = serviceHistoryQuery?.data?.systempartner?.integration === 'full';

  const apiUrl = `${localconfig.apiBaseUrl}/op/v1/service-history-request/${serviceHistoryQuery?.data?.id}`;

  const serviceHistoryRequestStatusCodes = statusCodes?.service_history_request || {};

  const serviceHistoryDocumentId = serviceHistoryQuery.data?.service_activity?.service?.documents?.filter(
    (document) => document?.type === 'service_history',
  )[0]?.id;

  const deleteDocumentMutation = useDeleteServiceDocumentMutation(serviceHistoryDocumentId);
  const uploadFileMutation = useUploadFileMutation(apiUrl, useServiceHistoryQuery.getKey(serviceHistoryId));
  const updateServiceHistoryRequestStatus = useUpdateServiceHistoryRequestStatusMutation(serviceHistoryId);
  const hasOpenTask = serviceHistoryQuery?.data?.open_task != undefined;

  const dateTaskCheckedByUser = serviceHistoryQuery.data?.open_task?.last_checked_at;
  const openTaskId = serviceHistoryQuery.data?.open_task?.id;
  const checkTaskMutation = useCheckTaskMutation(openTaskId as number, useServiceHistoryQuery.getKey(serviceHistoryId));
  const assignMutation = useAssignMutation(serviceHistoryQuery.data?.open_task?.id as number, useServiceHistoryQuery.getKey(serviceHistoryId));

  const assignNextTaskMutation = useAssignNextTaskMutation(type ?? '');
  const workshopLanguage = serviceHistoryQuery.data?.workshop?.language;

  const showAutomationInProgress =
    serviceHistoryQuery.data?.status === 100 &&
    serviceHistoryQuery.data?.open_task &&
    serviceHistoryQuery.data?.open_task.user_id == null &&
    serviceHistoryQuery.data?.open_task.automate === true &&
    serviceHistoryQuery.data?.open_task.queued === true;

  const handleAttachFallback = () => {
    NiceModal.show(AlertModal as any, {
      onSubmit: () => {
        attachFallbackMutation.mutate(undefined, {
          onSuccess: () => {
            toast({
              title: 'Success!',
              description: t('service_history_request:fallback.success'),
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
            queryClient.invalidateQueries({
              queryKey: useServiceHistoryQuery.getKey(serviceHistoryId),
            });
          },
          onError: (error) => {
            manageErrorResponse(toast, error as unknown as string);
          },
        });
      },
      children: t('service_history_request:fallback.modal.attach_fallback_warning'),
      content: {
        header: t('service_history_request:fallback.modal.attach_fallback_confirm'),
        footer: {
          buttons: {
            cancelCaption: t('common:cancel'),
            actionCaption: t('service_history_request:fallback.label'),
          },
        },
      },
    });
  };

  if (!serviceHistoryId || isNaN(serviceHistoryId) || serviceHistoryQuery.isError) {
    navigate('/404', { replace: true });
    return null;
  }

  if (accountQuery.isLoading || serviceHistoryQuery.isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <>
      {hasOpenTask ? (
        <TaskBannerCard
          showAutomationInProgress={showAutomationInProgress}
          queryToInvalidate={queryToInvalidate}
          opUsers={opUsers}
          currentUserId={user?.id}
          assignee={serviceHistoryQuery.data?.open_task?.assignee}
          isLoading={serviceHistoryQuery.isFetching}
          assign={() =>
            assignMutation.mutate({
              updated_at: get(serviceHistoryQuery.data, 'open_task.updated_at', serviceHistoryQuery.data?.updated_at)!,
            })
          }
          reassign={(user_id) =>
            assignMutation.mutate({
              force: true,
              user_id,
              updated_at: get(serviceHistoryQuery.data, 'open_task.updated_at', serviceHistoryQuery.data?.updated_at)!,
            })
          }
          task={serviceHistoryQuery.data?.open_task as Task}
        />
      ) : null}
      <ContentLayout
        sidebar={
          <>
            {type ? (
              <Button
                variant="primary"
                disabled={serviceHistoryQuery.isFetching || !isMember('tasks:assign-next')}
                data-test-id={`service_history_request_assign_button`}
                onClick={() =>
                  assignNextTaskMutation.mutate(
                    //@ts-ignore
                    {},
                    {
                      onSuccess: (task: Task) => {
                        if (task?.id) {
                          navigate(`/app/op/service-history-request/${task.subject_id}/${type}`);
                        } else {
                          navigate(`/app/op/break`);
                        }
                      },
                    },
                  )
                }
              >
                {t('common:next_activity')}
              </Button>
            ) : null}
            <WorkshopCard
              statusCodes={statusCodes?.workshop}
              isLoading={serviceHistoryQuery.isFetching}
              workshop={serviceHistoryQuery.data?.workshop}
            />
            <CredentialCard
              credential={accountQuery.data}
              isLoading={serviceHistoryQuery.isFetching}
              // @ts-ignore
              queryKey={useAccountV2Query.getKey()}
              sendRequest={(formData) =>
                sendRequest({
                  url: `${localconfig.apiBaseUrl}/op/v1/account/${accountQuery.data?.id}`,
                  dataForEndpoint: formData,
                })
              }
            />
          </>
        }
      >
        <PageMetatags title={t('pages:verification.service_history_request.details.meta.title')} />
        {serviceHistoryQuery.data?.status === 100 &&
          serviceHistoryQuery.data?.open_task === null &&
          (accountQuery.data?.status === 0 || accountQuery.data?.status === 100 || accountQuery.data?.status === 150) && (
            <Warning bgColor={'purple.100'} data-test-id="no-open-task-sr">
              <Stack textAlign={'center'} alignItems={'center'}>
                <HStack>
                  <Box display={'flex'}>
                    <Icon color={'orange'} as={IoIosWarning} boxSize="16" />
                  </Box>
                  <Box>
                    <Trans i18nKey={`tasks:open_task.no_open_task`} />
                  </Box>
                </HStack>
              </Stack>
            </Warning>
          )}
        <WorkshopWarning workshop={serviceHistoryQuery.data?.workshop} isLoading={serviceHistoryQuery.isFetching} />

        <StatusCard
          permission="service-history-requests:update"
          canEdit={Boolean(serviceHistoryQuery.data?.open_task?.user_id === user?.id)}
          completedAt={dateTimeTransformer(serviceHistoryQuery.data?.completed_at ?? '')}
          currentStatus={serviceHistoryQuery.data?.status}
          currentStatusReason={serviceHistoryQuery.data?.status_reason}
          fileId={serviceHistoryQuery.data?.file_id}
          id={serviceHistoryId}
          isLoading={serviceHistoryQuery.isFetching}
          reasons={statusCodes?.service_history_request_reasons}
          resourceKey={'service_history_request'}
          shouldHaveFile={true}
          statusCodes={statusCodes?.service_history_request}
          statusTransitions={statusTransitions?.service_history_request}
          updateStatus={(data: { [key: string]: any; status?: number | string }) => {
            const statusIntFormat = { ...data, status: Number(data['status']) };
            updateServiceHistoryRequestStatus.mutate(statusIntFormat);
          }}
        />
        {isSystemPartnerFull && <SystemPartnerCard name={serviceHistoryQuery?.data?.systempartner?.name as string} />}
        {hasOpenTask &&
        (serviceHistoryQuery.data?.status === getStatus(serviceHistoryRequestStatusCodes, 'on_hold') ||
          serviceHistoryQuery.data?.status === getStatus(serviceHistoryRequestStatusCodes, 'clarification')) ? (
          <RecentlyCheckedCard
            dateTaskCheckedByUser={dateTaskCheckedByUser}
            checkTask={() => {
              checkTaskMutation.mutate();
            }}
          />
        ) : null}
        <ContentCard
          isLoading={serviceHistoryQuery.isFetching}
          icon={<ServiceHistoryRequestIcon boxSize={10} />}
          minH="fit-content"
          header={`${t('service_history_request:header')}( ${serviceHistoryQuery.data?.user_readable_id} )`}
          // @ts-ignore
          contentDescription={
            <HStack justifyContent={'space-between'}>
              <Link to={`/app/op/workshops/${serviceHistoryQuery.data?.workshop_id}`}>{t('service_history_request:workshops.title')}</Link>
              <Text>
                {t('forms:submitted_at.label', {
                  date: dateTimeTransformer(get(serviceHistoryQuery.data, 'submitted_at', '-')),
                })}
              </Text>
            </HStack>
          }
          data-test-id="service-history-detail-card"
        >
          <Stack w={'100%'} data-test-id="service-history-information">
            <Stack w="full" spacing={2}>
              <Heading size={'xxs'}>{t('service_history_request:title')}</Heading>
              <Heading size={'xxs'}>{t('forms:vehicle.label')}</Heading>
              <div role="button" tabIndex={0} onClick={() => setApplyAnnimation(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setApplyAnnimation(true); }}>
                <InputCopyField
                  copy={{
                    currentCopiedValue: value,
                    handleCopy: copy,
                  }}
                  field={{
                    value: get(serviceHistoryQuery.data?.vehicle, 'vin', '-'),
                    label: t('forms:vin.label'),
                  }}
                  aria-label={'vin'}
                />
              </div>

              {workshopLanguage?.toLocaleLowerCase() !== 'de' && (
                <LanguageHighlight workshopLanguage={workshopLanguage as string} applyAnnimation={applyAnnimation} />
              )}
            </Stack>
          </Stack>
        </ContentCard>
        <ContentCard header={t('service_history_request:file.title')}>
          <Stack w={'full'}>
            {!serviceHistoryQuery.data?.file_id || canEdit ? (
              <FileForm
                deleteServiceDocumentMutation={deleteDocumentMutation}
                uploadFileMutation={uploadFileMutation}
                serviceDocumentExists={!!serviceHistoryDocumentId}
                setCanEdit={setCanEdit}
                editCancelButton={
                  serviceHistoryQuery.data?.file_id && (
                    <Button onClick={() => setCanEdit(false)} variant={'outline'}>
                      {t('common:cancel')}
                    </Button>
                  )
                }
              />
            ) : (
              <FileDisplay
                apiUrl={apiUrl}
                handleReplace={() => setCanEdit(true)}
                record={serviceHistoryQuery.data}
                canEdit={isMember('service-history-requests:update')}
                resourceKey={'service-history-request'}
              />
            )}
          </Stack>
          <Flex justifyContent="flex-end">
            <Button
              color="white"
              bgColor={'green.500'}
              size="md"
              isLoading={attachFallbackMutation.isPending}
              disabled={
                !isMember('service-history-requests:update') ||
                serviceHistoryQuery.data?.status !== 200 ||
                Boolean(serviceHistoryQuery.data?.open_task?.user_id !== user?.id)
              }
              _hover={{ bg: 'green.400' }}
              onClick={handleAttachFallback}
            >
              {t('service_history_request:fallback.label')}
            </Button>
          </Flex>
        </ContentCard>
        <TasksTableCard tasks={tasks} isLoading={serviceHistoryQuery.isPending} />
        <ActivityLogCard
          workshopName={serviceHistoryQuery.data?.workshop?.name}
          resourceKey={'service_history_request'}
          queryKey={useServiceHistoryQuery.getKey(serviceHistoryId)}
          activities={serviceHistoryQuery.data?.activities}
          account_id={serviceHistoryId}
        />
      </ContentLayout>
    </>
  );
};

export default ServiceHistoryDetailsPage;
